import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, RefreshCw, CheckCircle, Activity } from 'lucide-react';
import { auth, db } from '../config/firebase';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, getDoc, deleteDoc, setDoc } from 'firebase/firestore';
import { FIRESTORE_PATHS, CLOUD_FUNCTIONS } from '../config/appConfig';
import { useToast } from '../contexts/ToastContext';
import logger from '../utils/logger';

const VerifyOTP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [timer, setTimer] = useState(60);
  const inputRefs = useRef([]);
  const { success: showSuccess } = useToast();

  // Get signup data from navigation state
  const signupData = location.state?.signupData;

  useEffect(() => {
    if (!signupData) {
      navigate('/signup');
      return;
    }

    // Start countdown timer
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [signupData, navigate]);

  const handleChange = (index, value) => {
    if (value.length > 1) {
      value = value[0];
    }

    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = pastedData.split('');
    while (newOtp.length < 6) newOtp.push('');
    setOtp(newOtp);

    // Focus last filled input
    const lastIndex = Math.min(pastedData.length, 5);
    inputRefs.current[lastIndex]?.focus();
  };

  const verifyOTP = async () => {
    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setError('');
    setLoading(true);

    try {
      // Check OTP in Firestore
      const otpDocRef = doc(db, FIRESTORE_PATHS.EMAIL_VERIFICATIONS, signupData.email);
      const otpDoc = await getDoc(otpDocRef);

      if (!otpDoc.exists()) {
        setError('OTP expired or invalid. Please request a new one.');
        setLoading(false);
        return;
      }

      const otpData = otpDoc.data();

      // Check if OTP matches
      if (otpData.otp !== otpCode) {
        setError('Invalid OTP. Please try again.');
        setLoading(false);
        return;
      }

      // Check if OTP is expired (10 minutes)
      const now = Date.now();
      const otpCreatedAt = otpData.createdAt.toMillis();
      if (now - otpCreatedAt > 10 * 60 * 1000) {
        setError('OTP has expired. Please request a new one.');
        await deleteDoc(otpDocRef);
        setLoading(false);
        return;
      }

      // OTP is valid - create Firebase account
      setSuccess(true);

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        signupData.email,
        signupData.password
      );

      await updateProfile(userCredential.user, {
        displayName: signupData.name
      });

      // Create user profile directly on Users/{userId} (consolidated document)
      // Subscription will be created by UserContext on first load
      const userDocRef = doc(db, FIRESTORE_PATHS.USERS, userCredential.user.uid);
      await setDoc(userDocRef, {
        email: signupData.email,
        displayName: signupData.name,
        photoURL: null,
        createdAt: new Date().toISOString(),
        onboardingComplete: false
      }, { merge: true });

      // Clean up OTP document
      await deleteDoc(otpDocRef);

      // Redirect to dashboard with free plan already assigned and force reload
      setTimeout(() => {
        window.location.href = '/trackai/';
      }, 1500);

    } catch (err) {
      logger.error('VerifyOTP', 'Verification error:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError('This email is already registered. Please login instead.');
      } else {
        setError(err.message || 'Failed to verify OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const resendOTP = async () => {
    setResendLoading(true);
    setError('');

    try {
      // Call the sendOTP function again
      const response = await fetch(CLOUD_FUNCTIONS.getFunctionUrl(CLOUD_FUNCTIONS.SEND_OTP), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: signupData.email,
          name: signupData.name
        })
      });

      const data = await response.json();

      if (data.success) {
        setTimer(60);
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
        showSuccess('New OTP sent to your email!');
      } else {
        setError(data.message || 'Failed to resend OTP');
      }
    } catch (err) {
      logger.error('VerifyOTP', 'Resend error:', err);
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  if (!signupData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden opacity-10">
        <div className="absolute top-0 right-0 w-96 h-96 bg-green-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-600 rounded-full mb-4">
            <Activity size={32} className="text-white" />
          </div>
          <h1 className="text-3xl font-extrabold text-white mb-2">Verify Your Email</h1>
          <p className="text-gray-400">
            We've sent a 6-digit code to<br />
            <span className="text-white font-semibold">{signupData.email}</span>
          </p>
        </div>

        {/* OTP Card */}
        <div className="bg-gray-800 rounded-2xl p-8 shadow-2xl border border-gray-700">
          {!success ? (
            <>
              <div className="mb-6">
                <label className="block text-gray-300 text-sm font-medium mb-4 text-center">
                  Enter Verification Code
                </label>
                <div className="flex gap-2 justify-center" onPaste={handlePaste}>
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      className="w-12 h-14 text-center text-2xl font-bold bg-gray-900 border-2 border-gray-700 rounded-lg text-white focus:outline-none focus:border-green-500 transition-colors"
                      disabled={loading}
                    />
                  ))}
                </div>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500 rounded-lg p-3 mb-4">
                  <p className="text-red-400 text-sm text-center">{error}</p>
                </div>
              )}

              <button
                onClick={verifyOTP}
                disabled={loading || otp.join('').length !== 6}
                className="w-full bg-green-600 hover:bg-green-500 text-white font-bold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-4"
              >
                {loading ? 'Verifying...' : 'Verify & Create Account'}
              </button>

              {/* Resend OTP */}
              <div className="text-center">
                {timer > 0 ? (
                  <p className="text-gray-400 text-sm">
                    Resend code in <span className="text-white font-semibold">{timer}s</span>
                  </p>
                ) : (
                  <button
                    onClick={resendOTP}
                    disabled={resendLoading}
                    className="text-green-500 hover:text-green-400 text-sm font-semibold flex items-center justify-center mx-auto disabled:opacity-50"
                  >
                    <RefreshCw size={16} className={`mr-2 ${resendLoading ? 'animate-spin' : ''}`} />
                    {resendLoading ? 'Sending...' : 'Resend Code'}
                  </button>
                )}
              </div>

              <div className="mt-6 pt-6 border-t border-gray-700">
                <div className="flex items-start text-gray-400 text-xs">
                  <Mail size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                  <p>
                    Didn't receive the email? Check your spam folder or try resending the code.
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500/20 rounded-full mb-4">
                <CheckCircle size={40} className="text-green-500" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Email Verified!</h3>
              <p className="text-gray-400 mb-4">Creating your account...</p>
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500 mx-auto"></div>
            </div>
          )}
        </div>

        <p className="text-center text-gray-500 text-xs mt-6">
          This code expires in 10 minutes
        </p>
      </div>
    </div>
  );
};

export default VerifyOTP;




